import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import { SupervisorAccount, Email } from '@mui/icons-material';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { MainLayout } from '../components/layout/MainLayout';
import { db } from '../lib/firebase';
import { AdminListItem, AuthProfile, UserProfile } from '../types/auth';

export const AdminPage: React.FC = () => {
  const [admins, setAdmins] = useState<AdminListItem[]>([]);
  const [loading, setLoading] = useState(true);

  // 관리자 목록 조회
  useEffect(() => {
    const fetchAdmins = async () => {
      setLoading(true);
      try {
        // 1. auth_profiles에서 관리자 목록 조회
        const authQuery = query(collection(db, 'auth_profiles'), where('isAdmin', '==', true));
        const authSnapshot = await getDocs(authQuery);

        const authProfiles = authSnapshot.docs.map((doc) => ({
          uid: doc.id,
          ...doc.data(),
        })) as AuthProfile[];

        // 2. 각 관리자의 user_profiles 조회
        const adminListPromises = authProfiles.map(async (authProfile) => {
          try {
            const userProfileDoc = await getDoc(doc(db, 'user_profiles', authProfile.uid));

            let userProfile: UserProfile | null = null;
            if (userProfileDoc.exists()) {
              userProfile = userProfileDoc.data() as UserProfile;
            }

            // 3. 이메일 조회
            const email = await fetchUserEmail(authProfile.uid);

            return {
              uid: authProfile.uid,
              isAdmin: authProfile.isAdmin,
              adminType: authProfile.adminType,
              organization: userProfile?.organization || '정보 없음',
              name: userProfile?.name,
              email,
            } as AdminListItem;
          } catch (error) {
            console.error(`Error fetching profile for ${authProfile.uid}:`, error);
            return {
              uid: authProfile.uid,
              isAdmin: authProfile.isAdmin,
              adminType: authProfile.adminType,
              organization: '조회 실패',
              email: '조회 실패',
            } as AdminListItem;
          }
        });

        const adminList = await Promise.all(adminListPromises);

        // 관리자 타입별 정렬 (super > route1 > route2)
        adminList.sort((a, b) => {
          const order = { super: 0, route1: 1, route2: 2 };
          return order[a.adminType] - order[b.adminType];
        });

        setAdmins(adminList);
      } catch (error) {
        console.error('Error fetching admins:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAdmins();
  }, []);

  // 이메일 조회 함수
  const fetchUserEmail = async (uid: string): Promise<string> => {
    try {
      const response = await fetch(
        'https://us-central1-bpnpshuttle.cloudfunctions.net/getUserEmailHttp',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ uid }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch email');
      }

      const data = await response.json();
      return data.email;
    } catch (error) {
      console.error('Error fetching email:', error);
      return '조회 실패';
    }
  };

  // 관리자 타입 라벨
  const getAdminTypeLabel = (adminType: string): string => {
    switch (adminType) {
      case 'super':
        return '최고 관리자';
      case 'route1':
        return '강서구청 관리자';
      case 'route2':
        return '하단 관리자';
      default:
        return adminType;
    }
  };

  // 관리자 타입 색상
  const getAdminTypeColor = (
    adminType: string
  ): 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' | 'default' => {
    switch (adminType) {
      case 'super':
        return 'error';
      case 'route1':
        return 'primary';
      case 'route2':
        return 'secondary';
      default:
        return 'default';
    }
  };

  return (
    <MainLayout>
      <Box>
        {/* 헤더 */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SupervisorAccount sx={{ fontSize: 32, color: '#667eea' }} />
            <Box>
              <Typography variant="h4" component="h1" fontWeight={700}>
                관리자 관리
              </Typography>
              <Typography variant="body2" color="text.secondary">
                시스템 관리자 목록을 조회합니다.
              </Typography>
            </Box>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* 통계 카드 */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 3 }}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <SupervisorAccount sx={{ fontSize: 32, color: '#667eea' }} />
                  <Typography variant="h6" fontWeight={600}>
                    총 관리자
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} color="primary">
                  {admins.length}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  등록된 관리자 수입니다.
                </Typography>
              </Paper>

              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <SupervisorAccount sx={{ fontSize: 32, color: '#ef4444' }} />
                  <Typography variant="h6" fontWeight={600}>
                    최고 관리자
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} sx={{ color: '#ef4444' }}>
                  {admins.filter((a) => a.adminType === 'super').length}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  모든 권한을 가집니다.
                </Typography>
              </Paper>

              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <SupervisorAccount sx={{ fontSize: 32, color: '#667eea' }} />
                  <Typography variant="h6" fontWeight={600}>
                    강서구청
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} sx={{ color: '#667eea' }}>
                  {admins.filter((a) => a.adminType === 'route1').length}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  강서구청 노선 관리자
                </Typography>
              </Paper>

              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <SupervisorAccount sx={{ fontSize: 32, color: '#764ba2' }} />
                  <Typography variant="h6" fontWeight={600}>
                    하단
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} sx={{ color: '#764ba2' }}>
                  {admins.filter((a) => a.adminType === 'route2').length}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  하단 노선 관리자
                </Typography>
              </Paper>
            </Box>

            {/* 관리자 목록 테이블 */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                관리자 목록
              </Typography>

              {admins.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography color="text.secondary">관리자 정보가 없습니다.</Typography>
                </Box>
              ) : (
                <TableContainer sx={{ mt: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>이메일</TableCell>
                        <TableCell align="center">관리자 타입</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {admins.map((admin) => (
                        <TableRow key={admin.uid} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Email sx={{ fontSize: 18, color: '#10b981' }} />
                              <Typography variant="body2">{admin.email || '이메일 없음'}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={getAdminTypeLabel(admin.adminType)}
                              color={getAdminTypeColor(admin.adminType)}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </>
        )}
      </Box>
    </MainLayout>
  );
};
